/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { Column, ColumnInstance, TableInstance, useTable } from 'react-table';
import { OperationType, Parameter } from '../../types/SetpointManagerTypes';
import { useAppSelector } from '../../hooks/storeHooks';

interface WriteConfirmGroupProps {
  data: any[];
  name: string;
  operation?: OperationType;
}
const WriteConfirmGroup: React.FC<WriteConfirmGroupProps> = ({ data, name, operation = 'write' }) => {
  const writeItem = useAppSelector((state) => state.parameterGroups.writeItems);
  const columns: Column<Parameter>[] = useMemo(() => {
    const baseColumns: Column<Parameter>[] = [
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Parameter</span>
            </div>
          </div>
        ),
        accessor: 'parameter' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{value}</span>,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Description</span>
            </div>
          </div>
        ),
        accessor: 'description' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{value}</span>,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Last scan time</span>
            </div>
          </div>
        ),
        accessor: 'lastScanDate' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{value || '-'}</span>,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>{operation === 'backup' ? 'Current value' : 'Controller value'}</span>
            </div>
          </div>
        ),
        accessor: 'currentValue' as const,
        Cell: ({ row }: { row: { original: Parameter } }) => {
          return operation === 'backup' ? row.original.backupValue : row.original.currentValue;
        },
      } as any,
    ];
    if (operation === 'restore') {
      const restoreColumns: Column<Parameter>[] = [
        ...baseColumns,
        {
          Header: () => (
            <div className='parameter-table__header-content'>
              <div className='parameter-table__header-row'>
                <span className='parameter-table__header-text'>Restore value</span>
              </div>
            </div>
          ),
          // Custom id for alignment and rendering
          id: 'newValue',
          Cell: ({ row }: { row: { original: Parameter } }) => {
            return row.original.backupValue;
          },
        } as any,
      ];
      return restoreColumns;
    } else if (operation === 'backup') {
      // Replace the writeValue column with a New Value column showing currentValue
      const backupColumns: Column<Parameter>[] = [
        ...baseColumns,
        {
          Header: () => (
            <div className='parameter-table__header-content'>
              <div className='parameter-table__header-row'>
                <span className='parameter-table__header-text'>New Value</span>
              </div>
            </div>
          ),
          // Custom id for alignment and rendering
          id: 'newValue',
          Cell: ({ row }: { row: { original: Parameter } }) => {
            return row.original.currentValue;
          },
        } as any,
      ];
      return backupColumns;
    } else {
      // Default write flow: show Changed value from writeItem
      const writeColumns: Column<Parameter>[] = [
        ...baseColumns,
        {
          Header: () => (
            <div className='parameter-table__header-content'>
              <div className='parameter-table__header-row'>
                <span className='parameter-table__header-text'>Changed value</span>
              </div>
            </div>
          ),
          accessor: 'writeValue' as const,
          Cell: ({ row }: { row: { original: Parameter } }) => {
            return writeItem[row.original.parameter];
          },
        } as any,
      ];
      return writeColumns;
    }
  }, [operation, writeItem]);
  const instance = useTable({
    columns,
    data: data,
  });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = instance as TableInstance<Parameter> & {
    state: (typeof instance)['state'];
  };
  return (
    <article className='parameter-group mb-[16px]'>
      <header className={`parameter-group__header`}>
        <div className='parameter-group__title-container'>
          <h2 className='parameter-group__title'>{name}</h2>
        </div>

        <div className='parameter-group__parameter-count-container'>
          <div className='parameter-group__parameter-count-wrapper'>
            <div className='parameter-group__parameter-count-badge'>
              <span className='parameter-group__parameter-count-text'>{data.length} parameters</span>
            </div>
          </div>
        </div>
      </header>
      <section className={`parameter-table`} {...getTableProps()}>
        {headerGroups.map((headerGroup) => (
          <div className='parameter-table__header-row-container' {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((col) => {
              const column = col as unknown as ColumnInstance<Parameter>;
              return (
                <div className={`parameter-table__column parameter-table__column--${column.id}`}>
                  <div
                    className={`parameter-table__header-cell ${
                      column.id === 'select' || column.id === 'backupValue' || column.id === 'backupValueColumn'
                        ? 'parameter-table__header-cell--center'
                        : ''
                    }`}
                  >
                    <div className='flex items-center'>{column.render('Header')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div className='parameter-table__body' {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <div className='parameter-table__data-row' {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <div
                    className={`parameter-table__column parameter-table__column--${cell.column.id}`}
                    {...cell.getCellProps()}
                  >
                    <div
                      className={`parameter-table__data-cell ${
                        cell.column.id === 'select'
                          ? 'parameter-table__data-cell--center'
                          : cell.column.id === 'currentValue' ||
                              cell.column.id === 'writeValue' ||
                              cell.column.id === 'newValue'
                            ? 'parameter-table__data-cell--end'
                            : ''
                      }`}
                    >
                      {cell.render('Cell')}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
    </article>
  );
};
export default WriteConfirmGroup;
